import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getTeeColors } from '@/utils/teeUtils';
import usePageTitle from '@/hooks/usePageTitle';
import { resolveType } from '@/services/ServiceContainer';
import { CourseService } from '@/services/CourseService';
import { CourseModel } from '@/models/CourseModels';

const CourseView: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { theme } = useTheme();
  const [courseModel, setCourseModel] = useState<CourseModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Set page title based on course name or loading state
  usePageTitle(courseModel ? `${courseModel.name} - Course Details` : 'Loading Course...');

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;
      try {
        const courseService = resolveType(CourseService);
        const courseModel = await courseService.get(courseId);
        if (courseModel) {
          setCourseModel(courseModel);
        } else {
          setError('Course not found');
        }

      } catch (err) {
        setError('Failed to load course');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId]);

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: theme.spacing.XLARGE,
        color: theme.colors.TEXT
      }}>
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: theme.spacing.XLARGE,
        color: theme.colors.ERROR
      }}>
        {error}
      </div>
    );
  }

  if (!courseModel) {
    return (
      <div style={{
        textAlign: 'center',
        padding: theme.spacing.XLARGE,
        color: theme.colors.TEXT
      }}>
        No course found
      </div>
    );
  }

  return (
    <div style={{
      color: theme.colors.TEXT
    }}>
      {/* Course Title Container */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: theme.spacing.LARGE,
      }}>
        <h1 style={{ marginBottom: theme.spacing.MEDIUM }}>{courseModel.name}</h1>
      </div>

      {/* Scorecard Section - Full Width Container */}
      <div style={{
        backgroundColor: theme.colors.SURFACE,
        padding: theme.spacing.LARGE,
        marginBottom: theme.spacing.LARGE,
        width: '100%'
      }}>
        {/* Scorecard Content Container */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <h2 style={{ marginBottom: theme.spacing.MEDIUM }}>Scorecard</h2>
        </div>

        {/* Scrollable Table Container */}
        <div style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          position: 'relative',
          margin: 0,
          padding: `0 ${theme.spacing.LARGE}px`
        }}>
          <table style={{
            borderCollapse: 'collapse',
            backgroundColor: theme.colors.BACKGROUND,
            whiteSpace: 'nowrap',
            width: 'max-content',
            minWidth: '100%',
            position: 'relative',
            margin: '0 auto'
          }}>
            <thead>
              <tr>
                <th style={{ padding: theme.spacing.SMALL, textAlign: 'left', borderBottom: `1px solid ${theme.colors.BORDER}` }}>Hole</th>
                {courseModel.scorecard.holes.slice(0, 9).map((hole) => (
                  <th key={hole.number} style={{ padding: theme.spacing.SMALL, textAlign: 'center', minWidth: '40px', borderBottom: `1px solid ${theme.colors.BORDER}` }}>
                    {hole.number}
                  </th>
                ))}
                <th style={{ padding: theme.spacing.SMALL, textAlign: 'center', backgroundColor: theme.colors.SURFACE, borderBottom: `1px solid ${theme.colors.BORDER}` }}>Out</th>
                {courseModel.scorecard.holes.slice(9).map((hole) => (
                  <th key={hole.number} style={{ padding: theme.spacing.SMALL, textAlign: 'center', minWidth: '40px', borderBottom: `1px solid ${theme.colors.BORDER}` }}>
                    {hole.number}
                  </th>
                ))}
                <th style={{ padding: theme.spacing.SMALL, textAlign: 'center', backgroundColor: theme.colors.SURFACE, borderBottom: `1px solid ${theme.colors.BORDER}` }}>In</th>
                <th style={{ padding: theme.spacing.SMALL, textAlign: 'center', backgroundColor: theme.colors.SURFACE, borderBottom: `1px solid ${theme.colors.BORDER}` }}>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: theme.spacing.SMALL, fontWeight: 'bold', borderBottom: `1px solid ${theme.colors.BORDER}` }}>Par</td>
                {courseModel.scorecard.holes.slice(0, 9).map((hole) => (
                  <td key={hole.number} style={{ padding: theme.spacing.SMALL, textAlign: 'center', borderBottom: `1px solid ${theme.colors.BORDER}` }}>
                    {hole.par}
                  </td>
                ))}
                <td style={{
                  padding: theme.spacing.SMALL,
                  textAlign: 'center',
                  backgroundColor: theme.colors.SURFACE,
                  fontWeight: 'bold',
                  borderBottom: `1px solid ${theme.colors.BORDER}`
                }}>
                  {courseModel.scorecard.holes.slice(0, 9).reduce((sum, hole) => sum + hole.par, 0)}
                </td>
                {courseModel.scorecard.holes.slice(9).map((hole) => (
                  <td key={hole.number} style={{ padding: theme.spacing.SMALL, textAlign: 'center', borderBottom: `1px solid ${theme.colors.BORDER}` }}>
                    {hole.par}
                  </td>
                ))}
                <td style={{
                  padding: theme.spacing.SMALL,
                  textAlign: 'center',
                  backgroundColor: theme.colors.SURFACE,
                  fontWeight: 'bold',
                  borderBottom: `1px solid ${theme.colors.BORDER}`
                }}>
                  {courseModel.scorecard.holes.slice(9).reduce((sum, hole) => sum + hole.par, 0)}
                </td>
                <td style={{
                  padding: theme.spacing.SMALL,
                  textAlign: 'center',
                  backgroundColor: theme.colors.SURFACE,
                  fontWeight: 'bold',
                  borderBottom: `1px solid ${theme.colors.BORDER}`
                }}>
                  {courseModel.scorecard.holes.reduce((sum, hole) => sum + hole.par, 0)}
                </td>
              </tr>
              {courseModel.scorecard.holes[0].tees.map((_, teeIndex) => {
                const teeName = courseModel.scorecard.holes[0].tees[teeIndex].name;
                const { bg: teeBgColor } = getTeeColors(teeName, {
                  currentHole: courseModel.scorecard.holes[0].number,
                  currentYards: courseModel.scorecard.holes[0].tees[teeIndex].yards,
                  courseModel: courseModel
                });
                const useColor = teeBgColor !== 'transparent';
                const isWhiteTee = teeName.toLowerCase().includes('white');

                return (
                  <tr key={teeIndex}>
                    <td style={{
                      padding: theme.spacing.SMALL,
                      fontWeight: 'bold',
                      ...getTeeColors(teeName)
                    }}>
                      {teeName}
                    </td>
                    {courseModel.scorecard.holes.slice(0, 9).map((hole) => {
                      const { bg, text } = getTeeColors(teeName, {
                        currentHole: hole.number,
                        currentYards: hole.tees[teeIndex].yards,
                        courseModel: courseModel
                      });
                      return (
                        <td key={hole.number} style={{
                          padding: theme.spacing.SMALL,
                          textAlign: 'center',
                          backgroundColor: bg,
                          color: text
                        }}>
                          <div
                            className="px-2 py-1 rounded text-sm font-medium"
                            style={{
                              backgroundColor: bg,
                              color: text,
                            }}
                          >
                            {getTeeColors(teeName, {
                              currentHole: hole.number,
                              currentYards: hole.tees[teeIndex].yards,
                              courseModel: courseModel
                            }).content}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{
                      padding: theme.spacing.SMALL,
                      textAlign: 'center',
                      backgroundColor: '#FFFFFF',
                      color: isWhiteTee || ['Yellow', 'Gold', 'Tan', 'Khaki', 'Silver', 'Platinum', 'Pink', 'Sage'].some(c =>
                        teeName.toLowerCase().includes(c.toLowerCase())
                      ) ? '#000000' : (useColor ? teeBgColor : theme.colors.TEXT),
                      fontWeight: 'bold'
                    }}>
                      {courseModel.scorecard.holes.slice(0, 9).reduce((sum, hole) => sum + hole.tees[teeIndex].yards, 0)}
                    </td>
                    {courseModel.scorecard.holes.slice(9).map((hole) => {
                      const { bg, text } = getTeeColors(teeName, {
                        currentHole: hole.number,
                        currentYards: hole.tees[teeIndex].yards,
                        courseModel: courseModel
                      });
                      return (
                        <td key={hole.number} style={{
                          padding: theme.spacing.SMALL,
                          textAlign: 'center',
                          backgroundColor: bg,
                          color: text
                        }}>
                          <div
                            className="px-2 py-1 rounded text-sm font-medium"
                            style={{
                              backgroundColor: bg,
                              color: text,
                            }}
                          >
                            {getTeeColors(teeName, {
                              currentHole: hole.number,
                              currentYards: hole.tees[teeIndex].yards,
                              courseModel: courseModel
                            }).content}
                          </div>
                        </td>
                      );
                    })}
                    <td style={{
                      padding: theme.spacing.SMALL,
                      textAlign: 'center',
                      backgroundColor: '#FFFFFF',
                      color: isWhiteTee || ['Yellow', 'Gold', 'Tan', 'Khaki', 'Silver', 'Platinum', 'Pink', 'Sage'].some(c =>
                        teeName.toLowerCase().includes(c.toLowerCase())
                      ) ? '#000000' : (useColor ? teeBgColor : theme.colors.TEXT),
                      fontWeight: 'bold'
                    }}>
                      {courseModel.scorecard.holes.slice(9).reduce((sum, hole) => sum + hole.tees[teeIndex].yards, 0)}
                    </td>
                    <td style={{
                      padding: theme.spacing.SMALL,
                      textAlign: 'center',
                      backgroundColor: '#FFFFFF',
                      color: isWhiteTee || ['Yellow', 'Gold', 'Tan', 'Khaki', 'Silver', 'Platinum', 'Pink', 'Sage'].some(c =>
                        teeName.toLowerCase().includes(c.toLowerCase())
                      ) ? '#000000' : (useColor ? teeBgColor : theme.colors.TEXT),
                      fontWeight: 'bold'
                    }}>
                      {courseModel.scorecard.holes.reduce((sum, hole) => sum + hole.tees[teeIndex].yards, 0)}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td style={{ padding: theme.spacing.SMALL, fontWeight: 'bold', borderTop: `1px solid ${theme.colors.BORDER}` }}>Handicap</td>
                {courseModel.scorecard.holes.slice(0, 9).map((hole) => (
                  <td key={hole.number} style={{ padding: theme.spacing.SMALL, textAlign: 'center', borderTop: `1px solid ${theme.colors.BORDER}` }}>
                    {hole.mensHandicap18}
                  </td>
                ))}
                <td style={{ padding: theme.spacing.SMALL, textAlign: 'center', backgroundColor: theme.colors.SURFACE, borderTop: `1px solid ${theme.colors.BORDER}` }}></td>
                {courseModel.scorecard.holes.slice(9).map((hole) => (
                  <td key={hole.number} style={{ padding: theme.spacing.SMALL, textAlign: 'center', borderTop: `1px solid ${theme.colors.BORDER}` }}>
                    {hole.mensHandicap18}
                  </td>
                ))}
                <td style={{ padding: theme.spacing.SMALL, textAlign: 'center', backgroundColor: theme.colors.SURFACE, borderTop: `1px solid ${theme.colors.BORDER}` }}></td>
                <td style={{ padding: theme.spacing.SMALL, textAlign: 'center', backgroundColor: theme.colors.SURFACE, borderTop: `1px solid ${theme.colors.BORDER}` }}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Location Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: theme.spacing.LARGE,
      }}>
        <div style={{
          backgroundColor: theme.colors.SURFACE,
          padding: theme.spacing.LARGE,
          borderRadius: '8px',
          marginBottom: theme.spacing.LARGE
        }}>
          <h2 style={{ marginBottom: theme.spacing.MEDIUM }}>Location</h2>
          <div style={{ marginBottom: theme.spacing.MEDIUM }}>
            <p>{courseModel.location.address.full}</p>
            <p>Phone: {courseModel.location.phoneNumber}</p>
            {courseModel.websiteUrl && (
              <p>
                <a
                  href={courseModel.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: theme.colors.PRIMARY }}
                >
                  Visit Website
                </a>
              </p>
            )}
          </div>

          {/* Google Maps Embed */}
          <div style={{
            width: '100%',
            height: '400px',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            <iframe
              title="Course Location"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(courseModel.location.address.full)}`}
            />
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: theme.spacing.LARGE,
      }}>
        <div style={{
          backgroundColor: theme.colors.SURFACE,
          padding: theme.spacing.LARGE,
          borderRadius: '8px',
        }}>
          <h2 style={{ marginBottom: theme.spacing.MEDIUM }}>Details</h2>

          {/* Course Information */}
          <div style={{ marginBottom: theme.spacing.MEDIUM }}>
            <h3 style={{
              fontSize: '1.1em',
              marginBottom: theme.spacing.SMALL,
              color: theme.colors.TEXT_SECONDARY
            }}>Course Information</h3>
            <p>Type: {courseModel.type}</p>
            <p>Year Built: {courseModel.details.yearBuilt}</p>
            <p>Architect: {courseModel.details.architect}</p>
            <p>Season: {courseModel.details.golfSeason.open} - {courseModel.details.golfSeason.closed}</p>
          </div>

          {/* Amenities */}
          <div>
            <h3 style={{
              fontSize: '1.1em',
              marginBottom: theme.spacing.SMALL,
              color: theme.colors.TEXT_SECONDARY
            }}>Amenities</h3>
            <p>Cart Rentals: {courseModel.details.rentals.carts ? 'Yes' : 'No'}</p>
            <p>Club Rentals: {courseModel.details.rentals.clubs ? 'Yes' : 'No'}</p>
            <p>Driving Range: {courseModel.details.practice.drivingRange ? 'Yes' : 'No'}</p>
            <p>Chipping Area: {courseModel.details.practice.chippingArea ? 'Yes' : 'No'}</p>
            <p>Putting Green: {courseModel.details.practice.puttingGreen ? 'Yes' : 'No'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseView;
